import { useEffect, useMemo, useState } from 'react';
import { XCloseIcon } from '@rsos/assets/icons';
import { ChevronLeftIcon } from '@rsos/base-assets/icons';
import { Box, Button, Text } from '@rsos/base-components';
import {
  EventPaneSidePane,
  EventPaneSidePaneBody,
  EventPaneSidePaneHeader,
} from '../EventPane.styles';
import { useFetchTranscriptionsQuery } from '../tabs/TranscriptTab/transcriptionSlice';
import type { TranscriptionStateCall } from '../tabs/TranscriptTab/transcriptionSlice/transcriptionSlice.types';
import TranscriptHistoryDetails from './TranscriptHistoryDetails';
import TranscriptHistoryList from './TranscriptHistoryList';

interface TranscriptHistoryPaneProps {
  callerId: string;
  callStartTime: number | null;
  isOpen: boolean;
  onClose: () => void;
  transcriptions: TranscriptionStateCall[];
}

type ViewMode = 'list' | 'details';

const TranscriptHistoryPane = ({
  callerId,
  callStartTime,
  isOpen,
  onClose,
  transcriptions,
}: TranscriptHistoryPaneProps) => {
  const [mode, setMode] = useState<ViewMode>('list');
  const [selected, setSelected] = useState<TranscriptionStateCall | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const limit = 10;
  const shouldFetchMore = isOpen && mode === 'list' && page > 1;
  const queryArg = useMemo(
    () => ({ callerId, callStartTime, page, limit }),
    [callerId, callStartTime, page],
  );
  const { data, isFetching } = useFetchTranscriptionsQuery(queryArg, {
    skip: !shouldFetchMore || !callerId,
  });

  useEffect(() => {
    if (!isOpen) {
      setMode('list');
      setSelected(null);
      setPage(1);
      setHasMore(true);
      return;
    }
    // When opening, always start in list view.
    setMode('list');
    setSelected(null);
  }, [isOpen]);

  useEffect(() => {
    // Reset pagination when switching caller/call.
    setPage(1);
    setHasMore(true);
  }, [callerId, callStartTime]);

  useEffect(() => {
    if (!shouldFetchMore) return;
    // While loading, `data` may be undefined; don't treat that as "no more pages".
    if (isFetching) return;
    const count = data?.transcriptions?.length;
    if (typeof count !== 'number') return;
    if (count < limit) setHasMore(false);
  }, [data, shouldFetchMore, isFetching, limit]);

  if (!isOpen) return null;

  const selectedStartTime =
    selected?.payload?.call_information?.call_start_time || 0;
  const selectedTitle = selectedStartTime
    ? new Date(selectedStartTime).toLocaleString()
    : 'Previous Call';
  const title =
    mode === 'details'
      ? selectedTitle
      : `Previous Calls (${transcriptions.length})`;

  return (
    <EventPaneSidePane>
      <EventPaneSidePaneHeader>
        <Box horizontal align="center" gridColumnGap="8px">
          <Box width="28px" height="28px" align="center" justify="center">
            {mode === 'details' && (
              <Button
                btnType="invisible"
                aria-label="Back to recent transcripts"
                onClick={() => {
                  setMode('list');
                  setSelected(null);
                }}
                size="xsmall"
              >
                <ChevronLeftIcon
                  height={8}
                  width={8}
                  viewboxHeight={16}
                  viewboxWidth={16}
                  color="primaryText"
                />
              </Button>
            )}
          </Box>
          <Text size="normal" fontWeight="semibold" color="primaryText">
            {title}
          </Text>
        </Box>
        <Box
          onClick={onClose}
          cursor="pointer"
          aria-label="Close transcript pane"
        >
          <XCloseIcon width={12} height={12} color="primaryText" />
        </Box>
      </EventPaneSidePaneHeader>

      <EventPaneSidePaneBody>
        {mode === 'list' ? (
          <TranscriptHistoryList
            transcriptions={transcriptions}
            onSelect={(t: TranscriptionStateCall) => {
              setSelected(t);
              setMode('details');
            }}
            hasMore={hasMore}
            isLoadingMore={isFetching}
            onLoadMore={() => {
              if (!hasMore || isFetching) return;
              setPage(p => p + 1);
            }}
          />
        ) : (
          <TranscriptHistoryDetails
            callerId={callerId}
            transcription={selected}
          />
        )}
      </EventPaneSidePaneBody>
    </EventPaneSidePane>
  );
};

export default TranscriptHistoryPane;
